import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Grid,
  Heading,
  HStack,
  Text,
  Textarea,
} from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { useEffect, useState } from "react";
import {
  MdArrowBack,
  MdCheckCircle,
  MdContentCopy,
  MdError,
} from "react-icons/md";
import { useNavigate, useParams } from "react-router-dom";
import { ServerConfig, serverService } from "services/serverService";
import {
  ToolExecutionAttempt,
  ToolExecutionLogDetail,
  toolExecutionLogsService,
} from "services/toolExecutionLogsService";

interface ToolExecutionLogDetailsProps {
  logId?: string;
  embedded?: boolean;
}

const ToolExecutionLogDetails = ({
  logId,
  embedded = false,
}: ToolExecutionLogDetailsProps) => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const effectiveId = logId || id;
  const [log, setLog] = useState<ToolExecutionLogDetail | null>(null);
  const [attempts, setAttempts] = useState<ToolExecutionAttempt[]>([]);
  const [serverName, setServerName] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copiedRequest, setCopiedRequest] = useState(false);
  const [copiedResponse, setCopiedResponse] = useState(false);

  const loadLog = async () => {
    if (!effectiveId) return;

    try {
      setLoading(true);
      const data = await toolExecutionLogsService.getLog(effectiveId);
      setLog(data);

      // The other attempts at this tool in the same turn — best-effort, the page works without them.
      try {
        setAttempts(await toolExecutionLogsService.getAttempts(effectiveId));
      } catch {
        setAttempts([]);
      }

      // Load server name
      const servers = await serverService.getAllServers();
      const server = servers.find((s: ServerConfig) => s.id === data.serverId);
      setServerName(server?.name || data.serverId);

      setError("");
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          t("app.toolExecutionLogs.details.loadFailed"),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (effectiveId) {
      loadLog();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveId]);

  const copyToClipboard = async (
    text: string,
    type: "request" | "response",
  ) => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === "request") {
        setCopiedRequest(true);
        setTimeout(() => setCopiedRequest(false), 2000);
      } else {
        setCopiedResponse(true);
        setTimeout(() => setCopiedResponse(false), 2000);
      }
    } catch (err) {
      console.error("Failed to copy to clipboard", err);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const formatDuration = (ms?: number) => {
    if (ms === undefined || ms === null) return "N/A";
    if (ms < 1000) return `${ms} ms`;
    return `${(ms / 1000).toFixed(2)} seconds`;
  };

  const formatJson = (jsonString: string) => {
    try {
      const parsed = JSON.parse(jsonString);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return jsonString;
    }
  };

  const sectionPadding = embedded ? 4 : 6;
  const sectionHeadingSize = embedded ? "sm" : "md";
  const rootGap = embedded ? 3 : 5;

  if (loading) {
    return (
      <Box p={embedded ? 2 : 0}>
        <Text textAlign="center" color={{ base: "gray.600", _dark: "white" }}>
          {t("app.toolExecutionLogs.details.loading")}
        </Text>
      </Box>
    );
  }

  if (!log) {
    return (
      <Box p={embedded ? 2 : 0}>
        <Text textAlign="center" color="red.600">
          {t("app.toolExecutionLogs.details.notFound")}
        </Text>
      </Box>
    );
  }

  return (
    <Flex direction="column" w="full" gap={rootGap}>
      {!embedded && (
        <Flex justify="space-between" align="center">
          <HStack>
            <Button
              variant="ghost"
              onClick={() => navigate("/admin/tool-execution-logs")}
              size="sm"
            >
              <MdArrowBack />
            </Button>
            <Heading
              size="2xl"
              fontWeight="bold"
              color={{ base: "navy.700", _dark: "white" }}
            >
              {t("app.toolExecutionLogs.details.title")}
            </Heading>
          </HStack>
        </Flex>
      )}

      {error && (
        <Box borderRadius="lg" bg="red.100" p={3}>
          <Text fontSize="sm" color="red.700">
            {error}
          </Text>
        </Box>
      )}

      {/* Basic Info */}
      <Card.Root>
        <Card.Body p={sectionPadding}>
          <Flex justify="space-between" align="center" mb={4}>
            <HStack>
              {log.isSuccess ? (
                <MdCheckCircle size={24} color="green" />
              ) : (
                <MdError size={24} color="red" />
              )}
              <Heading
                size={sectionHeadingSize}
                color={{ base: "navy.700", _dark: "white" }}
              >
                {t("app.toolExecutionLogs.details.executionInfo")}
              </Heading>
            </HStack>
            <Badge colorPalette={log.isSuccess ? "green" : "red"} size="lg">
              {log.isSuccess ? t("app.common.success") : t("app.common.failed")}
            </Badge>
          </Flex>

          <Grid
            templateColumns={{
              base: "1fr",
              md: embedded ? "1fr" : "repeat(2, 1fr)",
            }}
            gap={4}
          >
            <Box>
              <Text
                fontSize="sm"
                fontWeight="bold"
                color={{ base: "gray.600", _dark: "gray.400" }}
              >
                {t("app.toolExecutionLogs.details.server")}
              </Text>
              <Text fontSize="md" color={{ base: "navy.700", _dark: "white" }}>
                {serverName}
              </Text>
            </Box>
            <Box>
              <Text
                fontSize="sm"
                fontWeight="bold"
                color={{ base: "gray.600", _dark: "gray.400" }}
              >
                {t("app.toolExecutionLogs.details.tool")}
              </Text>
              <Text fontSize="md" color={{ base: "navy.700", _dark: "white" }}>
                {log.toolName}
              </Text>
            </Box>
            <Box>
              <Text
                fontSize="sm"
                fontWeight="bold"
                color={{ base: "gray.600", _dark: "gray.400" }}
              >
                {t("app.toolExecutionLogs.details.endpoint")}
              </Text>
              <Text fontSize="md" color={{ base: "navy.700", _dark: "white" }}>
                {log.endpoint}
              </Text>
            </Box>
            <Box>
              <Text
                fontSize="sm"
                fontWeight="bold"
                color={{ base: "gray.600", _dark: "gray.400" }}
              >
                {t("app.toolExecutionLogs.details.method")}
              </Text>
              <Badge colorPalette="blue">{log.httpMethod}</Badge>
            </Box>
            <Box>
              <Text
                fontSize="sm"
                fontWeight="bold"
                color={{ base: "gray.600", _dark: "gray.400" }}
              >
                {t("app.toolExecutionLogs.details.statusCode")}
              </Text>
              <Text fontSize="md" color={{ base: "navy.700", _dark: "white" }}>
                {log.httpStatusCode === 0
                  ? t("app.toolExecutionLogs.notDispatched")
                  : log.httpStatusCode}
              </Text>
            </Box>
            {(log.attempt !== undefined || log.failureKind) && (
              <Box>
                <Text
                  fontSize="sm"
                  fontWeight="bold"
                  color={{ base: "gray.600", _dark: "gray.400" }}
                >
                  {t("app.toolExecutionLogs.details.attempt")}
                </Text>
                <HStack>
                  {log.attempt !== undefined && (
                    <Badge
                      colorPalette={log.attempt > 1 ? "orange" : "gray"}
                      variant="subtle"
                    >
                      {t("app.toolExecutionLogs.attemptBadge", {
                        n: log.attempt,
                      })}
                    </Badge>
                  )}
                  {log.failureKind && (
                    <Badge
                      colorPalette={
                        log.failureKind === "none" ? "green" : "gray"
                      }
                      variant="subtle"
                    >
                      {t(
                        `app.toolExecutionLogs.failureKind.${log.failureKind}`,
                      )}
                    </Badge>
                  )}
                </HStack>
              </Box>
            )}
            <Box>
              <Text
                fontSize="sm"
                fontWeight="bold"
                color={{ base: "gray.600", _dark: "gray.400" }}
              >
                {t("app.toolExecutionLogs.details.executedAt")}
              </Text>
              <Text fontSize="md" color={{ base: "navy.700", _dark: "white" }}>
                {formatDate(log.executedAt)}
              </Text>
            </Box>
            <Box>
              <Text
                fontSize="sm"
                fontWeight="bold"
                color={{ base: "gray.600", _dark: "gray.400" }}
              >
                {t("app.toolExecutionLogs.duration")}
              </Text>
              <Text fontSize="md" color={{ base: "navy.700", _dark: "white" }}>
                {formatDuration(log.durationMs)}
              </Text>
            </Box>
            {log.userId && (
              <Box>
                <Text
                  fontSize="sm"
                  fontWeight="bold"
                  color={{ base: "gray.600", _dark: "gray.400" }}
                >
                  {t("app.toolExecutionLogs.details.userId")}
                </Text>
                <Text
                  fontSize="md"
                  color={{ base: "navy.700", _dark: "white" }}
                >
                  {log.userId}
                </Text>
              </Box>
            )}
          </Grid>
        </Card.Body>
      </Card.Root>

      {/* Request Data */}
      <Card.Root>
        <Card.Body p={sectionPadding}>
          <Flex justify="space-between" align="center" mb={4}>
            <Heading
              size={sectionHeadingSize}
              color={{ base: "navy.700", _dark: "white" }}
            >
              {t("app.toolExecutionLogs.details.requestData")}
            </Heading>
            <Button
              onClick={() =>
                log.requestBody && copyToClipboard(log.requestBody, "request")
              }
              size="sm"
              variant="ghost"
              colorPalette={copiedRequest ? "green" : "blue"}
            >
              {copiedRequest ? <MdCheckCircle /> : <MdContentCopy />}
              {copiedRequest ? t("app.common.copied") : t("app.common.copy")}
            </Button>
          </Flex>
          <Textarea
            value={
              log.requestBody
                ? formatJson(log.requestBody)
                : t("app.toolExecutionLogs.details.noRequest")
            }
            readOnly
            rows={embedded ? 10 : 15}
            fontFamily="monospace"
            fontSize="sm"
          />
        </Card.Body>
      </Card.Root>

      {/* Response Data */}
      <Card.Root>
        <Card.Body p={sectionPadding}>
          <Flex justify="space-between" align="center" mb={4}>
            <Heading
              size={sectionHeadingSize}
              color={{ base: "navy.700", _dark: "white" }}
            >
              {t("app.toolExecutionLogs.details.responseData")}
            </Heading>
            <Button
              onClick={() =>
                log.responseBody &&
                copyToClipboard(log.responseBody, "response")
              }
              size="sm"
              variant="ghost"
              colorPalette={copiedResponse ? "green" : "blue"}
            >
              {copiedResponse ? <MdCheckCircle /> : <MdContentCopy />}
              {copiedResponse ? t("app.common.copied") : t("app.common.copy")}
            </Button>
          </Flex>
          <Textarea
            value={
              log.responseBody
                ? formatJson(log.responseBody)
                : t("app.toolExecutionLogs.details.noResponse")
            }
            readOnly
            rows={embedded ? 10 : 15}
            fontFamily="monospace"
            fontSize="sm"
          />
        </Card.Body>
      </Card.Root>

      {/* Attempts on this call — what the model tried, in order, and why each failed. A row that
          was never dispatched shows the structured error the model was given, which names the
          parameter and the expected shape: the signal for fixing a tool's description or example. */}
      {attempts.length > 1 && (
        <Card.Root>
          <Card.Body p={sectionPadding}>
            <Heading
              size={sectionHeadingSize}
              color={{ base: "navy.700", _dark: "white" }}
              mb={1}
            >
              {t("app.toolExecutionLogs.details.attemptsTitle", {
                n: attempts.length,
              })}
            </Heading>
            <Text
              fontSize="sm"
              color={{ base: "gray.600", _dark: "gray.400" }}
              mb={4}
            >
              {t("app.toolExecutionLogs.details.attemptsHelp")}
            </Text>
            <Flex direction="column" gap={3}>
              {attempts.map((a) => (
                <Box
                  key={a.id}
                  p={3}
                  borderWidth="1px"
                  borderRadius="md"
                  borderColor={
                    a.id === log.id
                      ? "blue.400"
                      : { base: "gray.200", _dark: "gray.700" }
                  }
                  cursor={a.id === log.id ? "default" : "pointer"}
                  onClick={() => {
                    if (a.id !== log.id && !embedded)
                      navigate(`/admin/tool-execution-logs/${a.id}`);
                  }}
                >
                  <HStack mb={2} flexWrap="wrap">
                    <Badge
                      colorPalette={a.isSuccess ? "green" : "red"}
                      variant="outline"
                    >
                      {t("app.toolExecutionLogs.attemptBadge", {
                        n: a.attempt ?? "?",
                      })}
                    </Badge>
                    <Badge
                      colorPalette={a.dispatched ? "blue" : "orange"}
                      variant="subtle"
                    >
                      {a.dispatched
                        ? a.httpStatusCode
                        : t("app.toolExecutionLogs.notDispatched")}
                    </Badge>
                    {a.failureKind && (
                      <Badge
                        colorPalette={
                          a.failureKind === "none" ? "green" : "gray"
                        }
                        variant="subtle"
                      >
                        {t(
                          `app.toolExecutionLogs.failureKind.${a.failureKind}`,
                        )}
                      </Badge>
                    )}
                    {a.subAgentLabel && (
                      <Badge colorPalette="purple" variant="subtle">
                        {a.subAgentLabel}
                      </Badge>
                    )}
                    <Text
                      fontSize="xs"
                      color={{ base: "gray.600", _dark: "gray.400" }}
                    >
                      {formatDate(a.executedAt)}
                    </Text>
                  </HStack>
                  <Grid
                    templateColumns={{
                      base: "1fr",
                      md: embedded ? "1fr" : "repeat(2, 1fr)",
                    }}
                    gap={3}
                  >
                    <Box>
                      <Text
                        fontSize="xs"
                        fontWeight="bold"
                        color={{ base: "gray.600", _dark: "gray.400" }}
                        mb={1}
                      >
                        {t("app.toolExecutionLogs.details.attemptArguments")}
                      </Text>
                      <Textarea
                        value={
                          a.requestBody
                            ? formatJson(a.requestBody)
                            : t("app.toolExecutionLogs.details.noRequest")
                        }
                        readOnly
                        rows={5}
                        fontFamily="monospace"
                        fontSize="xs"
                      />
                    </Box>
                    <Box>
                      <Text
                        fontSize="xs"
                        fontWeight="bold"
                        color={{ base: "gray.600", _dark: "gray.400" }}
                        mb={1}
                      >
                        {a.dispatched
                          ? t("app.toolExecutionLogs.details.attemptResponse")
                          : t("app.toolExecutionLogs.details.attemptRejection")}
                      </Text>
                      <Textarea
                        value={
                          a.responseBody
                            ? formatJson(a.responseBody)
                            : t("app.toolExecutionLogs.details.noResponse")
                        }
                        readOnly
                        rows={5}
                        fontFamily="monospace"
                        fontSize="xs"
                      />
                    </Box>
                  </Grid>
                </Box>
              ))}
            </Flex>
          </Card.Body>
        </Card.Root>
      )}
    </Flex>
  );
};

export default ToolExecutionLogDetails;
